import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { renderLinkPath } from '../../../helpers/general';
import Validator from '../../common/forms/Validator';
import ErrorMessage from '../../common/forms/ErrorMessage';
import Loader from '../../common/ui/Loader';
import Edit from '../../common/forms/buttons/Edit';
import Cancel from '../../common/forms/buttons/Cancel';
import Save from '../../common/forms/buttons/Save';
import defaultMessages from '../../common/forms/Validator/defaultMessages';

const initialAssessmentData = {
  name: '',
  en_typeform_url: '',
  es_typeform_url: '',
  skill_ids: []
};

export default class AssessmentItem extends Validator {
  static propTypes = {
    assessment: PropTypes.shape({
      name: PropTypes.string,
      en_typeform_url: PropTypes.string,
      es_typeform_url: PropTypes.string,
      skills: PropTypes.array
    }),
    skillsList: PropTypes.array.isRequired,
    isCreating: PropTypes.bool,
    closeNewAssessmentForm: PropTypes.func,
    getAssessmentsList: PropTypes.func.isRequired,
    editAssessment: PropTypes.func,
    createAssessment: PropTypes.func
  };

  static defaultProps = {
    messages: {
      minLength: defaultMessages.required
    }
  };

  constructor (props) {
    super(props);
    this.initialData = initialAssessmentData;
    if (props.assessment) {
      const { id, name, en_typeform_url, es_typeform_url, skills } = props.assessment;
      const skill_ids = skills.map(skill => skill.id);
      this.initialData = { id, name, en_typeform_url, es_typeform_url, skill_ids };
    }
    this.state = {
      isLoading: false,
      isEditing: false,
      ...this.initialData
    };
  }

  validationRules = {
    name: { required: true },
    en_typeform_url: { required: true },
    es_typeform_url: { required: true },
    skill_ids: { minLength: 1 }
  };

  closeCreationAssessment = () => {
    this.setState({
      ...initialAssessmentData
    });
    this.props.closeNewAssessmentForm();
  };

  startEditing = () => {
    this.setState({
      isEditing: true
    });
  };

  cancelEditing = () => {
    this.setState({
      isEditing: false,
      ...this.initialData
    });
    this._resetErrors();
  };

  handleChange = event => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
    if (name === 'en_typeform_url' || name === 'es_typeform_url') {
      this.validationRules[name] = value ? { isUrl: true } : { required: true };
    }
  };

  handleChangeSkills = selectedSkills => {
    this.setState({
      skill_ids: selectedSkills.map(skillOption => skillOption.value)
    });
  };

  handleSubmit = e => {
    e.preventDefault();
    if (this.validate(this.validationRules)) {
      this.setState({
        isLoading: true
      });
      const { name, en_typeform_url, es_typeform_url, skill_ids } = this.state;
      const submitMethod = this.state.isEditing ? this.submitEditing : this.createAssessment;
      submitMethod({ name, en_typeform_url, es_typeform_url, skill_ids });
    }
  };

  submitEditing = formData => {
    const { editAssessment, getAssessmentsList } = this.props;
    editAssessment(this.state.id, formData).then(response => {
      this.setState({
        isLoading: false,
        isEditing: false
      });
      if (response) {
        getAssessmentsList().then(() => {
          const { id, name, en_typeform_url, es_typeform_url, skills } = this.props.assessment;
          const skill_ids = skills.map(skill => skill.id);
          this.initialData = { id, name, en_typeform_url, es_typeform_url, skill_ids };
          this.state = {
            ...this.initialData
          };
        });
      } else {
        this.setState({
          ...this.initialData
        });
      }
    });
  };

  createAssessment = formData => {
    const { getAssessmentsList, createAssessment } = this.props;
    createAssessment(formData).then(data => {
      if (data) {
        this.closeCreationAssessment();
        getAssessmentsList();
      }
    });
  };

  render () {
    const { isEditing, isLoading, name, en_typeform_url, es_typeform_url, skill_ids } = this.state;
    const { isCreating, skillsList, assessment } = this.props;
    const isEditingMode = isEditing || isCreating;
    const errors = this.validationErrors;
    return (
      <div className='mb-3 col-12 col-md-6 col-xl-4'>
        <form className='item-wrapper location-list-item' onSubmit={this.handleSubmit}>
          { isLoading ? (
            <Loader />
          ) : (
            <Fragment>
              <div className='mb-2'>
                <div className='d-flex justify-content-between align-items-center'>
                  { isEditingMode ? (
                    <div className='location-name-input-wrapper'>
                      <input
                        className='input'
                        type='text'
                        name='name'
                        value={name || ''}
                        placeholder='Name'
                        onChange={this.handleChange}
                      />
                    </div>
                  ) : (
                    <h4>{name}</h4>
                  )}
                  <div className='ml-1'>
                    { isCreating ? (
                      <Cancel onClick={this.closeCreationAssessment} />
                    ) : isEditing ? (
                      <div className='d-flex'>
                        <Cancel onClick={this.cancelEditing} />
                        <Save />
                      </div>
                    ) : (
                      <Edit onClick={this.startEditing} />
                    )}
                  </div>
                </div>
                <ErrorMessage errorText={errors.name} />
              </div>
              <div className='mb-2 d-flex align-items-center'>
                <label htmlFor='en-typeform-url-input' className='mr-2 assessment-typeform-label'>EN:</label>
                { isEditingMode ? (
                  <div className='w-100'>
                    <input
                      id='en-typeform-url-input'
                      className='input'
                      type='text'
                      name='en_typeform_url'
                      value={en_typeform_url || ''}
                      placeholder='Type form url EN'
                      onChange={this.handleChange}
                    />
                    <ErrorMessage errorText={errors.en_typeform_url} />
                  </div>
                ) : (
                  <a className='link-primary' target='_blank' href={renderLinkPath(en_typeform_url)}>{en_typeform_url}</a>
                )}
              </div>
              <div className='mb-2 d-flex align-items-center'>
                <label htmlFor='es-typeform-url-input' className='mr-2 assessment-typeform-label'>ES:</label>
                { isEditingMode ? (
                  <div className='w-100'>
                    <input
                      className='input'
                      type='text'
                      name='es_typeform_url'
                      value={es_typeform_url || ''}
                      placeholder='Type form url ES'
                      onChange={this.handleChange}
                    />
                    <ErrorMessage errorText={errors.es_typeform_url} />
                  </div>
                ) : (
                  <a className='link-primary' target='_blank' href={renderLinkPath(es_typeform_url)}>{es_typeform_url}</a>
                )}
              </div>
              <div className='mb-2 d-flex align-items-center'>
                <label className='mr-2'>Skills:</label>
                { isEditingMode ? (
                  <div className='w-100'>
                    <Select
                      multi
                      value={skill_ids}
                      onChange={this.handleChangeSkills}
                      options={skillsList.map(skill => ({ label: skill.skill_label, value: skill.id }))}
                      placeholder={'Choose Skills'}
                    />
                    <ErrorMessage errorText={errors.skill_ids} />
                  </div>
                ) : (
                  <Fragment>
                    {assessment && assessment.skills.length > 0 ? (
                      <div className='d-flex flex-wrap ml-2'>
                        { assessment.skills.map(skill => (
                          <div className='skill-icon has-tooltip' style={{ backgroundImage: `url('${skill.icon_url}')` }} key={skill.id}>
                            <div className='ui-tooltip text-md'>
                              {skill.reference_title}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <p>No skills</p>}
                  </Fragment>
                )}
              </div>
              { isCreating && (
                <button type='submit' className='mt-2 w-100 button button-primary'>Add Assessment</button>
              )}
            </Fragment>
          )}
        </form>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/assessments/AssessmentItem.js